import { ButtonHTMLAttributes, CSSProperties, useState } from 'react';

/**
 * IconButton — quiet square icon action (theme toggle, sidebar collapse,
 * close buttons in headers). Transparent at rest in --text-soft; hover lifts
 * to --surface and brightens to --text.
 *
 * On a touch screen (see .sk-icon-btn in index.css) the tap area is at least
 * 44 x 44 pt and the icon at least 22 px, Apple's minimum for anything you tap.
 */
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Tabler icon name (without the `ti-` prefix), e.g. 'sun'. */
  icon: string;
  /** Icon font size in px on a mouse screen; touch screens use at least 22. Default 14. */
  iconSize?: number;
  /** Accessible label. */
  label?: string;
}

export function IconButton({ icon, iconSize = 14, label, style, ...props }: IconButtonProps) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      className="sk-icon-btn"
      aria-label={label}
      title={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? 'var(--surface)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: hover ? 'var(--text)' : 'var(--text-soft)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 6,
        borderRadius: 6,
        transition: 'background .15s, color .15s',
        ...style,
      }}
      {...props}
    >
      <i className={`ti ti-${icon} sk-icon`} style={{ '--icon-size': `${iconSize}px` } as CSSProperties} />
    </button>
  );
}

export default IconButton;
